import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Lazy-load toasters - not needed for FCP
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));

// Lazy load all pages including Index to reduce main bundle parse time
const Index = lazy(() => import("./pages/Index"));

// Lazy load non-critical routes to reduce initial bundle size
const Analytics = lazy(() => import("./pages/Analytics"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ForBars = lazy(() => import("./pages/ForBars"));
const Blogs = lazy(() => import("./pages/Blogs"));
const Neighborhoods = lazy(() => import("./pages/Neighborhoods"));
const NeighborhoodDetail = lazy(() => import("./pages/NeighborhoodDetail"));
const CategoryLanding = lazy(() => import("./pages/CategoryLanding"));
import { CATEGORY_LANDING_ROUTES } from "./lib/categoryLandingRoutes";
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Create QueryClient outside the component to persist across renders
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 30 * 60 * 1000, // Keep unused data in cache for 30 minutes (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      retry: 1,
    },
  },
});

// Minimal loading fallback for lazy-loaded routes
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/for-bars" element={<ForBars />} />
          <Route path="/blog" element={<Blogs />} />
          <Route path="/neighborhoods" element={<Neighborhoods />} />
          <Route path="/neighborhood/:neighborhoodId" element={<NeighborhoodDetail />} />
          {CATEGORY_LANDING_ROUTES.map((r) => (
            <Route
              key={r.path}
              path={r.path}
              element={
                <CategoryLanding
                  neighborhoodId={r.neighborhoodId}
                  categoryId={r.categoryId}
                />
              }
            />
          ))}
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
